"use client";

import React from "react";
import Image from "next/image";
import { getBrandConfig } from "@/lib/branding";

type LogoProps = {
  collapsed?: boolean;
  className?: string;
  variant?: "light" | "dark";
};

export const Logo: React.FC<LogoProps> = ({ collapsed = false, className, variant = "light" }) => {
  const textColor = variant === "dark" ? "#161616" : "white";
  const brand = getBrandConfig();
  const brandLetter = (brand.displayName || "?").trim().charAt(0).toUpperCase();

  type LogoRenderer = (args: {
    className?: string;
    textColor: string;
    brandLetter: string;
    brandName: string;
  }) => React.ReactNode;

  const TrustExamCollapsedLogo: LogoRenderer = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none" className={className}>
      <path
        d="M15.7798 8.66274C15.5486 9.36281 14.892 9.86756 14.1178 9.86756C13.1501 9.86756 12.3661 9.07967 12.3661 8.10708C12.3661 7.48664 12.6862 6.94087 13.1697 6.62733C12.6462 6.44597 12.0851 6.34664 11.5004 6.34664C8.66899 6.34664 6.37332 8.6537 6.37332 11.4992C6.37332 14.3446 8.66899 16.6516 11.5004 16.6516C14.3318 16.6516 16.6274 14.3446 16.6274 11.4992C16.6274 10.4502 16.3147 9.47605 15.7798 8.6619V8.66274Z"
        fill="#0059FF"
      />
      <path
        d="M22.9371 22.0808L20.8734 18.0158C22.1433 16.1634 22.8873 13.9195 22.8873 11.5C22.8873 5.15094 17.7153 -0.0245342 11.3975 8.74887e-05C5.11161 0.0247092 0.0204774 5.14355 6.05262e-05 11.4615C-0.0203563 17.8098 5.12714 23 11.4433 23C13.8557 23 16.0934 22.2491 17.9391 20.9679L22.207 22.8564C22.6921 23.0714 23.1772 22.5552 22.9363 22.0808H22.9371ZM11.4433 19.2148C7.20311 19.2148 3.76656 15.7604 3.76656 11.5C3.76656 7.23968 7.20392 3.78526 11.4433 3.78526C15.6826 3.78526 19.12 7.23968 19.12 11.5C19.12 15.7604 15.6826 19.2148 11.4433 19.2148Z"
        fill="#0059FF"
      />
    </svg>
  );

  const PlaceholderCollapsedLogo: LogoRenderer = ({ className, brandLetter }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none" className={className}>
      <circle cx="11.5" cy="11.5" r="11.5" fill="#0059FF" />
      <text
        x="11.5"
        y="15"
        textAnchor="middle"
        fontSize="12"
        fontFamily="Inter, Arial, sans-serif"
        fontWeight="700"
        fill="#FFFFFF"
      >
        {brandLetter}
      </text>
    </svg>
  );

  const TrustExamFullLogo: LogoRenderer = ({ className, textColor }) => (

    <svg viewBox="0 0 130 23" width="130" height="23" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M15.7798 8.66274C15.5486 9.36281 14.892 9.86756 14.1178 9.86756C13.1501 9.86756 12.3661 9.07967 12.3661 8.10708C12.3661 7.48664 12.6862 6.94087 13.1697 6.62733C12.6462 6.44597 12.0851 6.34664 11.5004 6.34664C8.66899 6.34664 6.37332 8.6537 6.37332 11.4992C6.37332 14.3446 8.66899 16.6516 11.5004 16.6516C14.3318 16.6516 16.6274 14.3446 16.6274 11.4992C16.6274 10.4502 16.3147 9.47605 15.7798 8.6619V8.66274Z"
        fill={textColor === '#161616' ? '#0059FF' : '#FFFFFF'}
      />
      <path
        d="M22.9371 22.0808L20.8734 18.0158C22.1433 16.1634 22.8873 13.9195 22.8873 11.5C22.8873 5.15094 17.7153 -0.0245342 11.3975 8.74887e-05C5.11161 0.0247092 0.0204774 5.14355 6.05262e-05 11.4615C-0.0203563 17.8098 5.12714 23 11.4433 23C13.8557 23 16.0934 22.2491 17.9391 20.9679L22.207 22.8564C22.6921 23.0714 23.1772 22.5552 22.9363 22.0808H22.9371ZM11.4433 19.2148C7.20311 19.2148 3.76656 15.7604 3.76656 11.5C3.76656 7.23968 7.20392 3.78526 11.4433 3.78526C15.6826 3.78526 19.12 7.23968 19.12 11.5C19.12 15.7604 15.6826 19.2148 11.4433 19.2148Z"
        fill={textColor === '#161616' ? '#0059FF' : '#FFFFFF'}
      />
      <path
        d="M114.284 18.2487V8.0054H117.251V9.2883H117.411C117.946 8.24591 118.841 7.72475 120.097 7.72475C120.712 7.72475 121.253 7.87843 121.721 8.1858C122.189 8.49317 122.55 8.92746 122.804 9.48876H122.964C123.579 8.31273 124.554 7.72475 125.891 7.72475C126.947 7.72475 127.768 8.05217 128.356 8.70696C128.958 9.36179 129.258 10.2572 129.258 11.3931V18.2487H126.291V12.0346C126.291 11.5134 126.158 11.1125 125.891 10.8318C125.623 10.5378 125.249 10.3908 124.768 10.3908C124.287 10.3908 123.913 10.5378 123.646 10.8318C123.378 11.1125 123.245 11.5134 123.245 12.0346V18.2487H120.278V12.0346C120.278 11.5134 120.144 11.1125 119.877 10.8318C119.623 10.5378 119.255 10.3908 118.774 10.3908C118.293 10.3908 117.919 10.5378 117.652 10.8318C117.385 11.1125 117.251 11.5134 117.251 12.0346V18.2487H114.284Z"
        fill={textColor}
      />
      <path
        d="M105.95 18.5293C105.015 18.5293 104.18 18.3021 103.445 17.8478C102.71 17.38 102.135 16.7386 101.721 15.9234C101.306 15.0948 101.099 14.1594 101.099 13.117C101.099 12.0746 101.306 11.1458 101.721 10.3307C102.135 9.51548 102.71 8.88069 103.445 8.42635C104.18 7.95859 105.015 7.72475 105.95 7.72475C106.699 7.72475 107.36 7.87843 107.935 8.1858C108.51 8.49317 108.957 8.94084 109.278 9.52885H109.438V8.0054H112.405V18.2487H109.438V16.7252H109.278C108.957 17.2999 108.51 17.7475 107.935 18.0683C107.36 18.3756 106.699 18.5293 105.95 18.5293ZM106.832 15.8632C107.621 15.8632 108.249 15.616 108.717 15.1216C109.198 14.6271 109.438 13.9589 109.438 13.117C109.438 12.2884 109.198 11.6269 108.717 11.1325C108.249 10.638 107.621 10.3908 106.832 10.3908C106.044 10.3908 105.409 10.638 104.928 11.1325C104.447 11.6269 104.206 12.2884 104.206 13.117C104.206 13.9589 104.447 14.6271 104.928 15.1216C105.409 15.616 106.044 15.8632 106.832 15.8632Z"
        fill={textColor}
      />
      <path
        d="M93.492 18.2487H90.2646L93.2915 13.0569L90.4252 8.00543H93.7929L95.4363 11.2728H95.6168L97.3005 8.00543H100.528L97.6413 13.0569L100.689 18.2487H97.3208L95.5367 14.8008H95.3765L93.492 18.2487Z"
        fill={textColor}
      />
      <path
        d="M82.2189 12.3954V15.4824H89.5155V18.2487H79.0916V4.2168H89.3951V6.9831H82.2189V9.78946H88.914V12.3954H82.2189Z"
        fill={textColor}
      />
      <path
        d="M77.3983 10.5713H74.9928V14.8811C74.9928 15.1885 75.0461 15.4023 75.153 15.5226C75.2734 15.6295 75.4869 15.6829 75.7944 15.6829H77.3983V18.2488H75.0526C73.957 18.2488 73.1754 18.0216 72.7073 17.5672C72.253 17.0995 72.0261 16.3177 72.0261 15.2219V10.5713H70.1416V8.00545H72.0261V4.91844H74.9928V8.00545H77.3983V10.5713Z"
        fill={textColor}
      />
      <path
        d="M64.6846 18.5293C63.6956 18.5293 62.8339 18.3689 62.099 18.0482C61.3637 17.7141 60.796 17.2598 60.395 16.6851C60.0074 16.1105 59.8004 15.4623 59.7736 14.7407H62.7403C62.7936 15.1149 62.9876 15.4156 63.3215 15.6427C63.6692 15.8566 64.1568 15.9635 64.7851 15.9635C65.8274 15.9635 66.3484 15.6762 66.3484 15.1015C66.3484 14.8075 66.2081 14.5937 65.9275 14.4601C65.6468 14.3131 65.1125 14.1861 64.3239 14.0792C62.8139 13.8921 61.7379 13.5647 61.0965 13.097C60.4686 12.6159 60.1543 11.9076 60.1543 10.9721C60.1543 9.96985 60.5687 9.18139 61.3971 8.60675C62.2393 8.01874 63.3284 7.72475 64.6647 7.72475C65.5468 7.72475 66.3349 7.8851 67.03 8.20585C67.7384 8.51322 68.2996 8.94084 68.714 9.48876C69.128 10.0367 69.3553 10.6581 69.3952 11.353H66.4285C66.3752 11.0189 66.1881 10.7583 65.8673 10.5712C65.5598 10.3708 65.119 10.2705 64.5443 10.2705C64.1035 10.2705 63.7558 10.3373 63.502 10.471C63.2613 10.5913 63.1413 10.7783 63.1413 11.0323C63.1413 11.3262 63.3015 11.5467 63.6224 11.6938C63.9429 11.8408 64.5578 11.961 65.4663 12.0546C66.8429 12.2016 67.8453 12.5156 68.4733 12.9967C69.1016 13.4778 69.4155 14.1861 69.4155 15.1216C69.4155 16.2174 68.9812 17.0593 68.1125 17.6473C67.2439 18.2353 66.1011 18.5293 64.6846 18.5293Z"
        fill={textColor}
      />
      <path
        d="M52.2884 18.5294C51.2192 18.5294 50.3705 18.1819 49.7426 17.487C49.1277 16.7921 48.8206 15.8365 48.8206 14.6204V8.00543H51.7874V13.8387C51.7874 14.4935 51.9407 14.9946 52.2481 15.3421C52.569 15.6896 53.0167 15.8633 53.5914 15.8633C54.166 15.8633 54.6138 15.6896 54.9342 15.3421C55.2551 14.9813 55.4153 14.4801 55.4153 13.8387V8.00543H58.3821V18.2487H55.4153V16.7854H55.2352C54.9411 17.3734 54.5402 17.8144 54.0322 18.1084C53.5243 18.389 52.9431 18.5294 52.2884 18.5294Z"
        fill={textColor}
      />
      <path
        d="M41.8541 18.2487V8.0054H44.8208V9.8095H44.981C45.1681 9.11461 45.4487 8.62684 45.8229 8.34619C46.2104 8.05221 46.792 7.90519 47.5671 7.90519H47.9278V10.8519H46.5447C45.3954 10.8519 44.8208 11.5201 44.8208 12.8565V18.2487H41.8541Z"
        fill={textColor}
      />
      <path d="M36.7315 18.2487H33.6044V7.04321H29.1343V4.2168H41.2016V7.04321H36.7315V18.2487Z" fill={textColor} />
    </svg>
  );

  const PlaceholderFullLogo: LogoRenderer = ({ className, textColor, brandName, brandLetter }) => (
    <svg viewBox="0 0 130 23" width="130" height="23" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <circle cx="11.5" cy="11.5" r="11.5" fill="#0059FF" />
      <text
        x="11.5"
        y="15"
        textAnchor="middle"
        fontSize="12"
        fontFamily="Inter, Arial, sans-serif"
        fontWeight="700"
        fill="#FFFFFF"
      >
        {brandLetter}
      </text>
      <text
        x="29"
        y="15"
        textAnchor="start"
        fontSize="14"
        fontFamily="Inter, Arial, sans-serif"
        fontWeight="700"
        fill={textColor}
      >
        {brandName}
      </text>
    </svg>
  );

  const ExamRuCollapsedLogo: LogoRenderer = ({ className, brandName }) => (
    <img
      className={className}
      src="/examru-logo-callapse.svg"
      alt={brandName || "ExamRu"}
      width={23}
      height={23}
      style={{ height: "23px", width: "auto" }}
    />
  );

  const ExamRuFullLogo: LogoRenderer = ({ className, brandName }) => (
    <img
      className={className}
      src={variant === "dark" ? "/examru-logo-dark.svg" : "/examru-logo-white.svg"}
      alt={brandName || "ExamRu"}
      width={228}
      height={80}
      style={{ height: "32px", width: "auto", marginBottom: "16px" }}
    />
  );

  const LOGO_BY_BRAND_KEY: Record<string, { collapsed: LogoRenderer; full: LogoRenderer }> = {
    trustexam: {
      collapsed: TrustExamCollapsedLogo,
      full: TrustExamFullLogo,
    },
    examru: {
      collapsed: ExamRuCollapsedLogo,
      full: ExamRuFullLogo,
    },
    default: {
      collapsed: PlaceholderCollapsedLogo,
      full: PlaceholderFullLogo,
    },
  };

  const brandName = (brand.displayName || "?").trim();
  const logoSet = LOGO_BY_BRAND_KEY[brand.key] ?? LOGO_BY_BRAND_KEY.default;
  const renderLogo = collapsed ? logoSet.collapsed : logoSet.full;

  return renderLogo({
    className,
    textColor,
    brandLetter,
    brandName,
  });
};
